// Stock / Price Data of all possiable SKU
/**
 * Example:
 * [
 *   {
 *     variant: {
 *       qwea: "poe",
 *       rqas: "13p",
 *     },
 *     variantDisplay: {
 *       Color: "White",
 *       Size: "12oz",
 *     },
 *     retailPrice: 1900,
 *     promoPrice: 1390,
 *     discountPercentage: 26.84,
 *      * variant: {
 *       qwea: "poe",
 *       rqas: "13p",
 *     },
 *     variantDisplay: {
 *       Color: "White",
 *       Size: "12oz",
 *     },
 *     retailPrice: 1900,
 *     promoPrice: 1390,
 *     discountPercentage: 26.84,
 *   }
 * ]
 */

// Avaliable options / variants to select
/**
 * [
 *   {
 *     variantKey: "q3",
 *     enableVariantImage: false,
 *     variantName: "Color",
 *     variantOptions: [
 *       {
 *         optionKey: "ert",
 *         optionName: "Luxe Black",
 *       },
 *       {
 *         optionKey: "QoE",
 *         optionName: "Angle White",
 *       },
 *       {
 *         optionKey: "EYf",
 *         optionName: "Magnetic Blue",
 *       },
 *     ],
 *   },
 *   {
 *     variantKey: "r5",
 *     enableVariantImage: false,
 *     variantName: "Size",
 *     variantOptions: [
 *       {
 *         optionKey: "8RQ",
 *         optionName: "12oz",
 *       },
 *       {
 *         optionKey: "VBa",
 *         optionName: "16oz",
 *       },
 *     ],
 *   },
 * ]
 */

import { decodeSkuKeyDict } from "@/shared.utils/products";
import {
  type ProductListingDetailed,
  type ProductSkuDetail,
  type ProductSkuPriceDetail,
  type ProductVariant,
  type ProductVariantKeyDict,
  type ProductVariantOption,
} from "@/stf.data/schema";

type VariantOptionWithDisabled = {
  disabled: boolean;
} & Pick<ProductVariantOption, "meta" | "optionKey" | "optionName">;

type UserVariantOption = {
  variantOptions: VariantOptionWithDisabled[];
} & Omit<ProductVariant, "advanceSettings" | "variantOptions" | "variantType">;

interface ReduceVariantStateReturn {
  displayPrice: ProductSkuPriceDetail;
  variantOptions: UserVariantOption[];
}

interface ReduceVariantStateProps {
  skuData: ProductSkuDetail[];
  userSelectedVariants: ProductVariantKeyDict | null;
  variant: ProductListingDetailed["variant"];
  variantArray: ProductListingDetailed["variantArray"];
}
/**
 * On user select variant, this function will return
 * the display price (lowest or selected) and
 * check stock available options to select.
 */
const reduceVariantState = (
  props: ReduceVariantStateProps,
): ReduceVariantStateReturn => {
  const {
    variant: productVariants,
    variantArray,
    skuData,
    userSelectedVariants: userSelectedVariantsWithNull,
  } = props;

  // converting null userSelectedVariants to empty dict
  const userSelectedVariants = (userSelectedVariantsWithNull === null) ? {} : userSelectedVariantsWithNull;

  // filter SKUs that are in stock
  const skusWithStock = skuData.filter((sku) => {
    return sku.stock > 0;
  });

  // helper function to check if a SKU matches the selected variants
  const isSkuMatching = (
    skuVariant: Record<string, string>,
    selectedVariants: Record<string, string>,
  ) => {
    return Object.entries(selectedVariants).every(
      ([key, value]) => { return skuVariant[key] === value; },
    );
  };

  // find SKUs matching the user's selected variants
  const matchingSkus = skusWithStock.filter((sku) => {
    return isSkuMatching(
      decodeSkuKeyDict(sku.variant),
      userSelectedVariants,
    );
  });

  // if product not sold out, filter based on sku with stock
  // else filter every possible sku
  const minPriceList = (skusWithStock.length && matchingSkus.length) ? matchingSkus : skuData;

  // find the SKU with the lowest promoPrice
  const skuWithMinPrice = minPriceList.reduce((minSku, sku) => {
    return sku.promoPrice < minSku.promoPrice ? sku : minSku;
  });

  // build the displayPrice object
  const displayPrice: ProductSkuPriceDetail = {
    variant: skuWithMinPrice.variant,
    variantDisplay: skuWithMinPrice.variantDisplay,
    retailPrice: skuWithMinPrice.retailPrice,
    promoPrice: skuWithMinPrice.promoPrice,
    discountPercentage: skuWithMinPrice.discountPercentage,
  };

  // build variantOptions with disabled status
  const variantOptions: UserVariantOption[] = variantArray.map(({ variantKey }) => {
    const variant = productVariants[variantKey];
    const {
      variantName,
      advanceSettings: {
        enableVariantImage,
      },
      variantOptions: variantOptionsConfig,
      variantOptionsArray,
    } = variant;

    const updatedVariantOptions: VariantOptionWithDisabled[]
      = variantOptionsArray.map(({ optionKey }) => {
        const option = variantOptionsConfig[optionKey];
        // Simulate selecting this option
        const updatedSelection = {
          ...userSelectedVariants,
          [variantKey]: optionKey,
        };

        // Check if any SKUs match this selection and are in stock
        const matchingSkusForOption = skusWithStock.filter((sku) => {
          return isSkuMatching(
            decodeSkuKeyDict(sku.variant),
            updatedSelection,
          );
        });

        // Option is disabled if no matching SKUs are found
        const disabled = matchingSkusForOption.length === 0;

        return {
          ...option,
          disabled,
        };
      });

    return {
      variantKey,
      variantName,
      enableVariantImage,
      variantOptions: updatedVariantOptions,
      variantOptionsArray,
    };
  });

  return {
    displayPrice,
    variantOptions,
  };
};

export {
  type ReduceVariantStateProps,
  type ReduceVariantStateReturn,
  reduceVariantState,
};
